import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../config/firebase';

// Initialiser les Cloud Functions
const functions = getFunctions();

// Service sécurisé pour remplacer l'accès direct à Firestore
class SecureApiService {
  constructor() {
    this.functions = {
      getSecureCodes: httpsCallable(functions, 'getSecureCodes'),
      securePurchaseCodes: httpsCallable(functions, 'securePurchaseCodes'),
      getSecureStats: httpsCallable(functions, 'getSecureStats'),
      getBillPaymentHistory: httpsCallable(functions, 'getBillPaymentHistory'),
      lookupAlgerieTelecomBills: httpsCallable(functions, 'lookupAlgerieTelecomBills'),
      submitBillPayment: httpsCallable(functions, 'submitBillPayment'),
      submitManualBillPaymentOrder: httpsCallable(functions, 'submitManualBillPaymentOrder'),
      submitTopupOrder: httpsCallable(functions, 'submitTopupOrder'),
      processTopupOrder: httpsCallable(functions, 'processTopupOrder'),
      claimTopupOrder: httpsCallable(functions, 'claimTopupOrder'),
      releaseTopupOrder: httpsCallable(functions, 'releaseTopupOrder')
    };
  }

  // Vérifier l'authentification avant chaque appel
  _checkAuth() {
    if (!auth.currentUser) {
      throw new Error('Utilisateur non authentifié');
    }
    return auth.currentUser;
  }

  _sanitizePublicMessage(message) {
    return String(message || '')
      .replace(/SofizPay/gi, 'service de paiement')
      .replace(/fournisseur/gi, 'service');
  }

  // Récupérer les codes de manière sécurisée
  async getCodes() {
    try {
      this._checkAuth();
      
      const result = await this.functions.getSecureCodes();
      
      if (result.data.success) {
        return {
          success: true,
          codes: result.data.codes,
          count: result.data.count
        };
      } else {
        throw new Error('Échec de récupération des codes');
      }
    } catch (error) {
      console.error('Erreur getCodes:', error);
      throw this._handleError(error);
    }
  }

  // Effectuer un achat sécurisé
  async purchaseCodes(productId, offerId, quantity) {
    try {
      this._checkAuth();
      
      if (!productId || !offerId || !quantity || quantity <= 0) {
        throw new Error('Paramètres d\'achat invalides');
      }

      const result = await this.functions.securePurchaseCodes({
        productId,
        offerId,
        quantity: parseInt(quantity)
      });

      if (result.data.success) {
        return {
          success: true,
          message: result.data.message,
          purchaseId: result.data.purchaseId,
          transactionId: result.data.transactionId || result.data.purchaseId,
          codes: result.data.codes,
          totalCost: result.data.totalCost,
          newBalance: result.data.newBalance
        };
      } else {
        throw new Error(result.data.message || 'Échec de l\'achat');
      }
    } catch (error) {
      console.error('Erreur purchaseCodes:', error);
      throw this._handleError(error);
    }
  }

  async submitTopupOrder(orderData) {
    try {
      this._checkAuth();

      const result = await this.functions.submitTopupOrder(orderData);

      if (result.data.success) {
        return {
          success: true,
          orderId: result.data.orderId,
          transactionId: result.data.transactionId,
          newBalance: result.data.newBalance,
          message: result.data.message
        };
      }

      throw new Error(result.data.message || 'Echec de la commande Topup');
    } catch (error) {
      console.error('Erreur submitTopupOrder:', error);
      throw this._handleError(error);
    }
  }

  async getBillPaymentHistory(limit = 20) {
    try {
      this._checkAuth();

      const result = await this.functions.getBillPaymentHistory({ limit });

      if (result.data.success) {
        return {
          success: true,
          items: result.data.items || []
        };
      }

      throw new Error(result.data.message || 'Impossible de charger l historique des paiements de facture');
    } catch (error) {
      console.error('Erreur getBillPaymentHistory:', error);
      throw this._handleError(error);
    }
  }

  async submitBillPayment(payload) {
    try {
      this._checkAuth();

      const result = await this.functions.submitBillPayment(payload);

      if (result.data.success) {
        return result.data;
      }

      throw new Error(result.data.message || 'Echec du paiement de facture');
    } catch (error) {
      console.error('Erreur submitBillPayment:', error);
      throw this._handleError(error);
    }
  }

  async lookupAlgerieTelecomBills(lineNumber) {
    try {
      this._checkAuth();

      const result = await this.functions.lookupAlgerieTelecomBills({ lineNumber });

      if (result.data.success) {
        return {
          success: true,
          lineNumber: result.data.lineNumber,
          bills: result.data.bills || []
        };
      }

      throw new Error(result.data.message || 'Impossible de charger les factures');
    } catch (error) {
      console.error('Erreur lookupAlgerieTelecomBills:', error);
      throw this._handleError(error);
    }
  }

  async submitManualBillPaymentOrder(payload) {
    try {
      this._checkAuth();

      const result = await this.functions.submitManualBillPaymentOrder(payload);

      if (result.data.success) {
        return result.data;
      }

      throw new Error(result.data.message || 'Echec de la demande de paiement de facture');
    } catch (error) {
      console.error('Erreur submitManualBillPaymentOrder:', error);
      throw this._handleError(error);
    }
  }

  async processTopupOrder(orderId, status, adminNote = '', proofImageUrl = '') {
    try {
      this._checkAuth();

      if (!orderId || !status) {
        throw new Error('Parametres de traitement Topup invalides');
      }

      const result = await this.functions.processTopupOrder({
        orderId,
        status,
        adminNote,
        proofImageUrl
      });

      if (result.data.success) {
        return {
          success: true,
          refunded: Boolean(result.data.refunded),
          refundAmount: Number(result.data.refundAmount || 0),
          message: result.data.message
        };
      }

      throw new Error(result.data.message || 'Echec du traitement de la commande Topup');
    } catch (error) {
      console.error('Erreur processTopupOrder:', error);
      throw this._handleError(error);
    }
  }

  async claimTopupOrder(orderId) {
    try {
      this._checkAuth();

      if (!orderId) {
        throw new Error('Commande Topup invalide');
      }

      const result = await this.functions.claimTopupOrder({ orderId });

      if (result.data.success) {
        return result.data;
      }

      throw new Error(result.data.message || 'Echec de la prise en charge de la commande Topup');
    } catch (error) {
      console.error('Erreur claimTopupOrder:', error);
      throw this._handleError(error);
    }
  }

  async releaseTopupOrder(orderId) {
    try {
      this._checkAuth();

      if (!orderId) {
        throw new Error('Commande Topup invalide');
      }

      const result = await this.functions.releaseTopupOrder({ orderId });

      if (result.data.success) {
        return result.data;
      }

      throw new Error(result.data.message || 'Echec du relachement de la commande Topup');
    } catch (error) {
      console.error('Erreur releaseTopupOrder:', error);
      throw this._handleError(error);
    }
  }

  // Récupérer les statistiques (admins uniquement)
  async getStats() {
    try {
      this._checkAuth();
      
      const result = await this.functions.getSecureStats();
      
      if (result.data.success) {
        return {
          success: true,
          stats: result.data.stats
        };
      } else {
        throw new Error('Échec de récupération des statistiques');
      }
    } catch (error) {
      console.error('Erreur getStats:', error);
      throw this._handleError(error);
    }
  }

  // Gestion centralisée des erreurs
  _handleError(error) {
    // Erreurs Firebase Functions
    if (error.code) {
      switch (error.code) {
        case 'functions/unauthenticated':
          return new Error('Vous devez être connecté pour effectuer cette action');
        case 'functions/permission-denied':
          return new Error('Vous n\'avez pas les permissions nécessaires');
        case 'functions/invalid-argument':
          return new Error('Données invalides fournies');
        case 'functions/failed-precondition':
          return new Error(this._sanitizePublicMessage(error.message || 'Conditions non remplies'));
        case 'functions/not-found':
          return new Error('Ressource introuvable');
        case 'functions/resource-exhausted':
          return new Error('Trop de demandes, veuillez réessayer plus tard');
        case 'functions/unavailable':
          return new Error('Service temporairement indisponible');
        default:
          return new Error(`Erreur systÃ¨me: ${this._sanitizePublicMessage(error.message)}`);
      }
    }
    
    // Erreurs réseau
    if (error.message.includes('network')) {
      return new Error('Problème de connexion réseau');
    }
    
    // Autres erreurs
    return new Error(this._sanitizePublicMessage(error.message || 'Une erreur inattendue s\'est produite'));
  }

  // Méthode pour tester la connectivité
  async testConnection() {
    try {
      this._checkAuth();
      
      // Test simple avec getCodes
      await this.functions.getSecureCodes();
      return { success: true, message: 'Connexion sécurisée établie' };
    } catch (error) {
      return { 
        success: false, 
        message: 'Échec de connexion aux services sécurisés',
        error: error.message 
      };
    }
  }
}

// Instance singleton
const secureApiService = new SecureApiService();

export default secureApiService;

// Exports nommés pour compatibilité
export {
  secureApiService,
  SecureApiService
};

// Exemple d'utilisation dans les composants:
/*
import secureApiService from '../services/secureApiService';

// Dans un composant React
const MyComponent = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadCodes = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await secureApiService.getCodes();
      setCodes(result.codes);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (productId, quantity) => {
    try {
      setLoading(true);
      
      const result = await secureApiService.purchaseCodes(productId, quantity);
      
      // Afficher succès
      alert(`Achat réussi! Transaction: ${result.transactionId}`);
      
      // Recharger les codes
      await loadCodes();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {error && <div className="error">{error}</div>}
      {loading && <div>Chargement...</div>}
      {codes.map(code => (
        <div key={code.id}>{code.name}</div>
      ))}
    </div>
  );
};
*/
